import * as React from 'react';
import { cn } from '@noor/lib/cn';

/**
 * CPanel navigation glyphs exported from the Noor Figma icon-rail, cleaned to
 * `currentColor` 24x24 line icons matching lucide-react's stroke-1.5 style.
 * Use like a lucide icon: `<MailboxIcon className="size-6" />`. Raw SVG sources
 * live in `./assets/icons`. Prefer lucide-react for generic glyphs; use these
 * only for the design's specific custom marks.
 */
type IconProps = React.ComponentProps<'svg'>;

function MailboxIcon({ className, ...props }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      className={cn('size-6 shrink-0', className)}
      {...props}
    >
      <path
        d="M21.0038 13.0004V18.0025C21.0038 19.66 19.66 21.0037 18.0025 21.0037H5.9975C4.33996 21.0037 2.99625 19.66 2.99625 18.0025V5.99747C2.99625 4.33992 4.33996 2.99622 5.9975 2.99622H10.9996"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M2.99625 15.0012H7.38008C7.75868 15.0012 8.10484 15.215 8.27445 15.5535L8.72164 16.4488C8.89113 16.788 9.23783 17.0022 9.61701 17.0021H14.382C14.7612 17.0022 15.1079 16.788 15.2774 16.4488L15.7246 15.5545C15.8941 15.2153 16.2407 15.001 16.6199 15.0012H21.0038"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <rect
        x="14.0008"
        y="1.99585"
        width="8.00333"
        height="8.00333"
        rx="2"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M16.752 4.74703H19.253"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M16.752 7.24801H19.253"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
}

function ChartIcon({ className, ...props }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      className={cn('size-6 shrink-0', className)}
      {...props}
    >
      <path
        d="M5 15.889L10.071 10.818C10.462 10.427 11.095 10.427 11.485 10.818L13.626 12.959C14.017 13.35 14.65 13.35 15.04 12.959L21 7"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M18.333 7H21V9.667"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M21 20.9289H1.925V3.22095"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
}

function BasketIcon({ className, ...props }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      className={cn('size-6 shrink-0', className)}
      {...props}
    >
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M19.6 6.467L17.9 4.2C17.333 3.445 16.444 3 15.5 3H8.5C7.556 3 6.667 3.445 6.1 4.2L4.4 6.467C4.14 6.813 4 7.234 4 7.667V18C4 19.657 5.343 21 7 21H17C18.657 21 20 19.657 20 18V7.667C20 7.234 19.86 6.813 19.6 6.467Z"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M15 10C15 11.657 13.657 13 12 13C10.343 13 9 11.657 9 10"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M19.88 7H4.12"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
}

function PackageDeliveryIcon({ className, ...props }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      className={cn('size-6 shrink-0', className)}
      {...props}
    >
      <path
        d="M3.99667 20.0033V20.0033C3.99667 17.517 6.01222 15.5015 8.49854 15.5015H11.0826"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M17.5023 15.0012V18.0025"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M21.0038 18.7808C21.0038 18.351 20.6553 18.0025 20.2254 18.0025H14.7792C14.3493 18.0025 14.0008 18.351 14.0008 18.7808"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M15.0013 22.0041H20.0033C20.5559 22.0041 21.0038 21.5562 21.0038 21.0037V17.4372C21.0037 17.0318 20.9217 16.6306 20.7627 16.2577L20.4845 15.6085C20.3272 15.2403 19.9655 15.0014 19.5652 15.0012H15.4394C15.0387 15.001 14.6765 15.24 14.5191 15.6085L14.2419 16.2567C14.0829 16.6296 14.0009 17.0308 14.0008 17.4362V21.0037C14.0008 21.5562 14.4487 22.0041 15.0013 22.0041Z"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M15.0063 5.24223C16.2224 6.45818 16.5862 8.28699 15.9282 9.87584C15.2701 11.4647 13.7197 12.5007 12 12.5007C10.2803 12.5007 8.72988 11.4647 8.07182 9.87584C7.41376 8.28699 7.77763 6.45818 8.99375 5.24223C9.79097 4.44475 10.8724 3.9967 12 3.9967C13.1276 3.9967 14.209 4.44475 15.0063 5.24223"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
}

function LocationArrowIcon({ className, ...props }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      className={cn('size-6 shrink-0', className)}
      {...props}
    >
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M13.845 18.313L17.907 7.35396C18.205 6.54996 17.422 5.76696 16.618 6.06496L5.654 10.13C4.73 10.473 4.803 11.803 5.758 12.043L10.698 13.284L11.931 18.208C12.171 19.164 13.502 19.237 13.845 18.313V18.313Z"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
}

function HelpChatIcon({ className, ...props }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      className={cn('size-6 shrink-0', className)}
      {...props}
    >
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M3 11.291C3 13.974 4.42 16.336 6.599 17.825C6.598 18.68 6.6 19.832 6.6 21.041L10.131 19.294C10.735 19.41 11.359 19.473 12 19.473C16.952 19.473 21 15.83 21 11.291C21 6.75201 16.952 3.10901 12 3.10901C7.048 3.10901 3 6.75201 3 11.291Z"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M11.899 12.328V12.121C11.899 11.443 12.318 11.076 12.738 10.794C13.148 10.518 13.559 10.158 13.559 9.49396C13.559 8.57696 12.816 7.83496 11.9 7.83496C10.984 7.83496 10.24 8.57596 10.24 9.49296"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M11.898 14.667C11.815 14.667 11.748 14.734 11.749 14.817C11.749 14.9 11.816 14.967 11.899 14.967C11.982 14.967 12.049 14.9 12.049 14.817C12.049 14.734 11.983 14.667 11.898 14.667"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
}

export {
  MailboxIcon,
  ChartIcon,
  BasketIcon,
  PackageDeliveryIcon,
  LocationArrowIcon,
  HelpChatIcon,
};
export type { IconProps as CpanelIconProps };
